import { useEffect, useMemo, useState } from "react";
import { TrendingUp } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import DateFilter from "./layout/DateFilter";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";

const ACCENT = "#EC4899";

function fmtTickDate(iso) {
  if (!iso) return "";
  // YYYY-MM-DD → "Mon D"
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function MoldOvertimeTrendCard({ showMexico = false, dept = "MOLD" }) {
  const [filter, setFilter] = useState({ preset: "all" });
  const [data, setData] = useState({ weeks: [] });
  const [loading, setLoading] = useState(true);

  const params = useMemo(() => {
    const p = { preset: filter.preset, show_mexico: showMexico };
    if (filter.preset === "custom") { p.start = filter.start; p.end = filter.end; }
    return p;
  }, [filter, showMexico]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/departments/${dept}/overtime-trend`, { params })
      .then((r) => !cancelled && setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [params, dept]);

  const weeks = data.weeks || [];

  return (
    <section
      className="relative bg-[#12141D] border border-border overflow-hidden p-5"
      data-testid="mold-overtime-trend-card"
    >
      <span className="absolute top-0 left-0 right-0 h-[2px]"
            style={{ background: `linear-gradient(90deg, ${ACCENT} 0%, ${ACCENT}66 100%)` }} />

      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <TrendingUp size={18} style={{ color: ACCENT }} />
          <h3 className="font-heading font-bold text-xl">{dept} Overtime Trend</h3>
        </div>
        <span className="text-[10px] tracking-widest uppercase text-muted-foreground">
          {weeks.length} week{weeks.length === 1 ? "" : "s"}
        </span>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Weekly OT hours from {dept === "MOLD" ? "Mold" : dept === "FRAME" ? "Frame" : dept === "GFRC" ? "GFRC" : dept} Hours Summary
      </p>

      <div className="mb-4">
        <DateFilter value={filter} onChange={setFilter} />
      </div>

      {loading && (
        <div className="h-[420px] flex items-center justify-center text-sm uppercase tracking-widest text-muted-foreground">
          Loading…
        </div>
      )}
      {!loading && weeks.length === 0 && (
        <div className="h-[420px] flex items-center justify-center text-sm uppercase tracking-widest text-muted-foreground"
             data-testid="ot-trend-empty">
          No overtime in range
        </div>
      )}
      {!loading && weeks.length > 0 && (
        <div className="h-[420px]" data-testid="ot-trend-chart">
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
            <LineChart data={weeks} margin={{ top: 10, right: 20, left: 10, bottom: 30 }}>
              <CartesianGrid stroke="#1F2937" strokeDasharray="3 3" />
              <XAxis
                dataKey="week_start" stroke="#94A3B8"
                tick={{ fontSize: 10, fill: "#94A3B8" }}
                tickFormatter={fmtTickDate}
                angle={-40} textAnchor="end" height={56} interval={0}
              />
              <YAxis
                stroke="#94A3B8" tick={{ fontSize: 10 }}
                label={{ value: "OT Hours", angle: -90, position: "insideLeft", fill: "#94A3B8", fontSize: 11 }}
              />
              <Tooltip
                contentStyle={{ backgroundColor: "#0A0B10", border: "1px solid #1F2937", fontSize: 12 }}
                labelStyle={{ color: "#E2E8F0" }}
                labelFormatter={(v) => `Week of ${fmtTickDate(v)}`}
                formatter={(v) => [`${v}h`, "Overtime"]}
              />
              <Line
                type="monotone" dataKey="ot"
                stroke={ACCENT} strokeWidth={3}
                dot={{ r: 4, fill: ACCENT, stroke: ACCENT }}
                activeDot={{ r: 6, fill: ACCENT }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </section>
  );
}
